import { Component, Input, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IonIcon } from '@ionic/angular/standalone';
import { SexyModule } from '@qid/sexy';
import { PlusMinusComponent } from '../../../../components/plus-minus/plus-minus.component';
import { TextUtil } from '@qid/core';
import { Share } from '../../model/Share';
import { IntegrationConfiguration, IntegrationConfigurationApi, IntegrationConfigurationFacade } from '../../../integration-configuration';
import { firstValueFrom, map } from 'rxjs';

export interface TIMEGuestStats {
    guestUpto20: number;
    guest21To40: number;
    guest41To60: number;
    guestAbove61: number;
    country: string;
    state: string;
    maleGuests: number;
    femaleGuests: number;
    numRooms: number;
    arrivalDate: string;  // e.g. "12-Feb-2025"
    departureDate: string; // e.g. "22-Feb-2025"
    accommodationChargesPerDay: number;
    purposeOfVisit: string;
}

@Component({
    selector: 'time-integration',
    templateUrl: './time-integration.component.html',
    styleUrls: ['./time-integration.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        SexyModule,
        PlusMinusComponent,
        IonIcon
    ]
})
export class TimeIntegrationComponent implements OnInit {

    @Input() share: Share;
    @Input() submitToTimes: boolean = true;
    @Input() checkInDate: string = '';
    @Input() checkOutDate: string = '';

    timeForm: FormGroup;
    private formBuilder = inject(FormBuilder);

    timeData = {
        numRooms: '',
        accommodationCharges: '',
        purposeOfVisit: '',
        country: '',
        state: ''
    };

    public timePurposeOfVisitOptions = TextUtil.getTimePurposeOfVisitOptions();
    public timeCountryOptions = TextUtil.getTimeCountryOptions();
    public timeStateOptions = TextUtil.getStatesOfIndia();

    // Time stats data
    timeStats: TIMEGuestStats | null = null;

    private integrationConfigurationApi = inject(IntegrationConfigurationApi);

    private integrationConfigurationFacade = inject(IntegrationConfigurationFacade);
    public integrationConfiguration = this.integrationConfigurationFacade.integrationConfiguration$;


    // Computed property to check if totals match
    get totalsMatch(): boolean {
        if (!this.timeStats) return true;

        const ageGroupTotal = this.timeStats.guestUpto20 + this.timeStats.guest21To40 +
            this.timeStats.guest41To60 + this.timeStats.guestAbove61;
        const genderTotal = this.timeStats.maleGuests + this.timeStats.femaleGuests;

        return ageGroupTotal === genderTotal;
    }

    // Computed property to check if state field should be shown
    get shouldShowStateField(): boolean {
        return this.timeData.country === 'India';
    }

    ngOnInit() {
        // Initialize form with validation
        this.initializeForm();
        // Calculate time stats
        this.calculateTimeStats();
    }

    private initializeForm() {
        this.timeForm = this.formBuilder.group({
            numRooms: ['', [Validators.required, Validators.min(1), Validators.pattern(/^\d+$/)]],
            accommodationCharges: ['', [Validators.required, Validators.min(0), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
            purposeOfVisit: ['', [Validators.required]],
            country: ['', [Validators.required]],
            state: ['', [Validators.required]]
        });

        // Subscribe to form value changes to update timeData
        this.timeForm.valueChanges.subscribe(value => {
            this.timeData = { ...this.timeData, ...value };

            // Reset state when country changes and is not India
            if (value.country && value.country !== 'India') {
                this.timeForm.patchValue({ state: '' }, { emitEvent: false });
                // Remove required validator for state when country is not India
                this.timeForm.get('state')?.clearValidators();
                this.timeForm.get('state')?.updateValueAndValidity();
            } else if (value.country === 'India') {
                // Add required validator for state when country is India
                this.timeForm.get('state')?.setValidators([Validators.required]);
                this.timeForm.get('state')?.updateValueAndValidity();
            }
        });
    }

    // Validation helper methods
    isFieldInvalid(fieldName: string): boolean {
        const field = this.timeForm.get(fieldName);
        return !!(field && field.invalid && (field.dirty || field.touched));
    }

    getFieldError(fieldName: string): string {
        const field = this.timeForm.get(fieldName);
        if (field && field.errors && (field.dirty || field.touched)) {
            if (field.errors['required']) {
                return `${this.getFieldLabel(fieldName)} is required`;
            }
            if (field.errors['min']) {
                return `${this.getFieldLabel(fieldName)} must be at least ${field.errors['min'].min}`;
            }
            if (field.errors['pattern']) {
                return `${this.getFieldLabel(fieldName)} format is invalid`;
            }
            if (field.errors['maxlength']) {
                return `${this.getFieldLabel(fieldName)} cannot exceed ${field.errors['maxlength'].requiredLength} characters`;
            }
        }
        return '';
    }

    private getFieldLabel(fieldName: string): string {
        const labels: { [key: string]: string } = {
            numRooms: 'Number of Rooms',
            accommodationCharges: 'Accommodation Charges',
            purposeOfVisit: 'Purpose of Visit',
            country: 'Country',
            state: 'State'
        };
        return labels[fieldName] || fieldName;
    }

    calculateTimeStats() {
        if (!this.share?.participants) {
            this.timeStats = null;
            return;
        }

        const participants = this.share.participants;

        let timesData: TIMEGuestStats = {
            guestUpto20: 0,
            guest21To40: 0,
            guest41To60: 0,
            guestAbove61: 0,
            country: '',
            state: '',
            maleGuests: 0,
            femaleGuests: 0,
            numRooms: 0,
            arrivalDate: '',
            departureDate: '',
            accommodationChargesPerDay: 0,
            purposeOfVisit: ''
        };

        participants.forEach(participant => {
            if (participant?.analytics?.age) {
                if (participant.analytics.age < 21) {
                    timesData.guestUpto20++;
                } else if (participant.analytics.age < 41) {
                    timesData.guest21To40++;
                } else if (participant.analytics.age < 61) {
                    timesData.guest41To60++;
                } else {
                    timesData.guestAbove61++;
                }
            }

            if (participant?.analytics?.gender) {
                if (participant.analytics.gender.toLowerCase() === 'male') {
                    timesData.maleGuests++;
                } else if (participant.analytics.gender.toLowerCase() === 'female') {
                    timesData.femaleGuests++;
                }
            }
        });

        this.timeStats = timesData;
    }

    getTimeData(): TIMEGuestStats | null {
        if (!this.timeStats) return null;

        // Format dates for TIME API (DD-MMM-YYYY format)
        const formatDateForTime = (dateString: string): string => {
            if (!dateString) return '';
            const date = new Date(dateString);
            const day = date.getDate().toString().padStart(2, '0');
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = monthNames[date.getMonth()];
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
        };

        return {
            ...this.timeStats,
            numRooms: parseInt(this.timeData.numRooms) || 1,
            accommodationChargesPerDay: parseFloat(this.timeData.accommodationCharges) || 2000,
            purposeOfVisit: this.timeData.purposeOfVisit || 'Official',
            country: this.timeData.country || 'India',
            state: this.timeData.country === 'India' ? (this.timeData.state || 'Goa') : '',
            arrivalDate: formatDateForTime(this.checkInDate),
            departureDate: formatDateForTime(this.checkOutDate)
        };
    }


    public async uploadTimesData(): Promise<any> {
        try {
            // Mark all fields as touched to trigger validation display
            this.timeForm.markAllAsTouched();

            // Check if form is valid
            if (!this.timeForm.valid) {
                console.error('Form is invalid. Please fix the errors before submitting.');
                return false;
            }

            // Check if totals match
            if (!this.totalsMatch) {
                console.error('Age group totals do not match gender totals. Please fix the data mismatch.');
                return false;
            }

            const config: IntegrationConfiguration[] = await firstValueFrom(this.integrationConfiguration);

            const timesConfig = config?.find(config => config.integrationName === 'TIME');

            const hotelAuthKey = timesConfig?.integrationConfig?.hotelAuthKey;
            const hotelId = timesConfig?.integrationConfig?.hotelId;
            const environment = timesConfig?.integrationConfig?.environment;
            const guestData = this.getTimeData();

            if (guestData && hotelAuthKey && hotelId) {
                const period = this.getPeriodFromDate(new Date(this.checkInDate));
                return this.integrationConfigurationApi.uploadTimesData(hotelAuthKey, hotelId, guestData, period, environment);
            } else {
                console.error('Missing required TIME configuration or guest data');
                return null;
            }
        } catch (error) {
            console.error('Error submitting TIME data:', error);
            return false;
        }
    }


    /**
     * Formats a date into period format "MON-YYYY" (e.g., "DEC-2025")
     * @param date - The date to format
     * @returns The formatted period string
     */
    getPeriodFromDate(date: Date): string {
        const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
        return `${monthNames[date.getMonth()]}-${date.getFullYear()}`;
    }

}
